/**
 * `<page-instance-settings>` — the instance's own settings, routed at
 * `/instances/:id/settings`.
 *
 * Implements the editable half of `SC-F309` (size is one knob), the cosmetic half
 * of `06` §3.4's `PATCH /v1/instances/{id}` (name and display name) and `SC-F821`
 * (the audit privacy profile: Standard, Minimized, and Customer-held staged to
 * `SC-F822`). Stories: `SC-S301`, `SC-S316`, `SC-S812`.
 *
 * **The page is organized by blast radius, not by subject**, because that is the
 * only grouping that answers the question a reader actually has before they press
 * save. Three tiers, and every one of them says which it is:
 *
 *   1. **Cosmetic.** The name and the display name. `02` §1.2 builds every cluster
 *      object's name from the record id, so renaming an instance is not a cluster
 *      operation at all — it answers `200` with the updated record and nothing
 *      restarts. The slug and the hostname are *not* here: they are fixed for the
 *      life of the instance, so a rename deliberately does not move the endpoint.
 *   2. **Restart-class.** The size class and the audit privacy profile. Both answer
 *      `202` with a job, both replace the running task, and both state that before
 *      the confirm rather than after the save. The size change moves the container
 *      limits, DuckDB's memory limit and its thread count *together* — they are
 *      derived jointly from one row precisely so they cannot be set independently
 *      (`SC-S301`) — and the profile change re-renders `server.yaml`, which never
 *      hot-reloads in any part.
 *   3. **Irreversible.** The danger zone.
 *
 * **The danger zone here names the operations and hands off the ceremony.** Restart,
 * suspend, resume and delete are performed on the instance console's own danger tab,
 * which already owns their confirmations — the typed instance name, the retained
 * versus destroyed lists, the asynchronous-deletion statement. A second copy of
 * those dialogs on this page is how one of them ends up wording the deletion
 * consequence differently from the other, and the wording *is* the safety
 * mechanism. So this section states what each operation costs and who may do it,
 * and its buttons navigate to the one place that performs it.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  toastPersistent,
  type BnKvRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import { ApiError, parseError, validationFields } from "../api/errors.js";
import { me } from "../api/auth.js";
import { atLeast, type Role } from "../api/orgs.js";
import {
  INSTANCE_SIZES,
  getInstanceWithETag,
  setAuditProfile,
  sizeClass,
  updateInstance,
  type AuditProfile,
  type Instance,
  type InstanceSize,
} from "../api/instances.js";
import { jobFailure, watchJob, type Job } from "../api/jobs.js";
import { hrefFor, navigate } from "../nav.js";

/**
 * PROFILES is `SC-F821`'s vocabulary with the disclosure each option owes the
 * reader, taken from `04` §2.3's derivation table.
 *
 * `customer_held` is present and **disabled**, not hidden. `SC-S812`'s first
 * acceptance criterion says so explicitly, and the reason is that hiding it would
 * make the question it answers — "can I keep the records myself?" — unanswerable
 * from the product: a regulated customer would ask support instead of reading that
 * it is coming.
 */
const PROFILES: ReadonlyArray<{
  value: AuditProfile;
  label: string;
  /** The one line the option carries inside the picker. */
  option: string;
  /** What the cloud stores under this profile, in full. */
  stores: string;
  /** The derived `audit.sqlSampleBytes`, which the customer never edits directly. */
  sample: string;
  available: boolean;
  unavailableReason?: string;
}> = [
  {
    value: "standard",
    label: "Standard",
    option: "Keeps a bounded sample of each query's text. The default.",
    stores:
      "Everything an audit record contains, including a bounded sample of the query text — the first 2048 bytes.",
    sample: "audit.sqlSampleBytes: 2048",
    available: true,
  },
  {
    value: "minimized",
    label: "Minimized",
    option: "No SQL text is captured at all. Recommended in a regulated environment.",
    stores:
      "Everything an audit record contains, minus the SQL text. The text is not captured at the source rather than captured and dropped on arrival, because “we delete it when it gets here” is a promise you cannot verify.",
    sample: "audit.sqlSampleBytes: 0",
    available: true,
  },
  {
    value: "customer_held",
    label: "Customer-held",
    option: "Records go to your own bucket; Sluice Cloud keeps none of them.",
    stores:
      "Nothing. Records go to your own bucket with your own credentials, and Sluice Cloud keeps only chain attestation.",
    sample: "your own value",
    available: false,
    unavailableReason:
      "Coming in a later release. It changes the ingest topology, the export path and the analytics story at once, so it ships as one piece or not at all.",
  },
];

/** profileLabel is the display word for a stored profile, falling back to the raw
 * enum member for a value this bundle does not know. */
function profileLabel(profile: AuditProfile | string): string {
  return PROFILES.find((p) => p.value === profile)?.label ?? profile;
}

@customElement("page-instance-settings")
export class PageInstanceSettings extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private instance: Instance | null = null;
  /** The record's `ETag`, sent back as `If-Match` so a concurrent edit by another
   * maintainer is a `412` rather than a silent overwrite (`06` §1.7). */
  @state() private etag = "";
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  /** The caller's role on this instance's organization. Named `orgRole` rather than
   * `role` because `HTMLElement.role` exists and shadowing it with a private field
   * makes the element unassignable to its own base type. */
  @state() private orgRole: Role | undefined;

  /* ── the cosmetic form ─────────────────────────────────────────────── */
  @state() private name = "";
  @state() private displayName = "";
  @state() private savingIdentity = false;
  @state() private identityFailure: ApiError | null = null;
  @state() private fieldErrors: Partial<Record<"name" | "display_name" | "size", string>> = {};

  /* ── the restart-class forms ───────────────────────────────────────── */
  @state() private size: InstanceSize = "medium";
  @state() private profile: AuditProfile = "standard";
  /** Which restart-class change is being confirmed, if any. */
  @state() private confirm: "size" | "profile" | null = null;
  @state() private acceptRestart = false;
  @state() private applying = false;
  @state() private changeFailure: ApiError | null = null;
  /** The job a restart-class change produced, watched inline; the permalink is the
   * durable view of it. */
  @state() private job: Job | null = null;

  private watchAbort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    .muted {
      margin: var(--bino-space-sm) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    a.inline {
      color: var(--bino-primary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .fields {
      display: grid;
      gap: var(--bino-space-md);
      max-width: 34rem;
    }
    .actions {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: center;
      flex-wrap: wrap;
      margin-top: var(--bino-space-md);
    }
    .row {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: center;
      flex-wrap: wrap;
    }
    /* The chosen profile's disclosure, always visible under the picker. A
       dropdown's own option lines carry the short form; this is the long one, and
       it is not behind a click because it is the decision. */
    .disclosure {
      display: grid;
      gap: var(--bino-space-xs);
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
      max-width: 72ch;
    }
    .disclosure p {
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .disclosure p strong {
      color: var(--bino-text);
    }
    .disclosure .sample {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    /* The danger zone. Bordered in the error tone so it cannot be mistaken for
       the cards above it, which is the whole point of a danger zone. */
    .danger-card {
      padding: var(--bino-space-lg);
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
    }
    .danger-card + .danger-card {
      margin-top: var(--bino-space-md);
    }
    .danger-card h2 {
      color: var(--bino-error);
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.watchAbort?.abort();
    this.watchAbort = undefined;
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) {
      this.watchAbort?.abort();
      this.instance = null;
      this.etag = "";
      this.failure = null;
      this.identityFailure = null;
      this.changeFailure = null;
      this.job = null;
      this.fieldErrors = {};
      if (this.instanceId) void this.load();
    }
  }

  private async load() {
    this.loading = true;
    try {
      const { value, etag } = await getInstanceWithETag(this.instanceId);
      this.adopt(value, etag);
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.instance = null;
    } finally {
      this.loading = false;
    }
    void this.loadRole();
  }

  /** adopt seeds the form from the record. Every field is re-seeded on every read,
   * including after a save, so the inputs can never drift from what is stored. */
  private adopt(value: Instance, etag: string) {
    this.instance = value;
    this.etag = etag;
    this.name = value.name;
    this.displayName = value.display_name ?? "";
    this.size = value.size;
    this.profile = value.audit_profile;
  }

  /**
   * The caller's role on this instance's organization. A failure leaves it
   * undefined, which pre-disables nothing: the server re-checks every route and
   * answers `forbidden` naming the role it needed and the role the caller holds, so
   * guessing pessimistically here would hide controls from people who may use them.
   */
  private async loadRole() {
    const org = this.instance?.org;
    if (!org) return;
    try {
      const identity = await me();
      this.orgRole = identity.organizations.find((o) => o.id === org)?.role;
    } catch {
      this.orgRole = undefined;
    }
  }

  private get canMaintain(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "maintainer");
  }

  private get canOwn(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "owner");
  }

  /* ── the cosmetic save ─────────────────────────────────────────────── */

  /**
   * saveIdentity sends only the cosmetic fields, and only the ones that changed.
   *
   * Sending an unchanged field would be harmless — the route compares before it
   * writes, and a patch that changes nothing writes nothing precisely so it does not
   * invalidate every other client's `If-Match` — but sending only what moved is what
   * makes the response's own classification meaningful: this call answers `200` and
   * never `202`, because neither field is material.
   */
  private async saveIdentity() {
    const inst = this.instance;
    if (!inst) return;
    const name = this.name.trim();
    const display = this.displayName.trim();
    // Every checked field gets an explicit entry, empty when it passes, so a
    // message from the previous attempt cannot survive a fix. Merging only the
    // failures is how a corrected field keeps showing its old error.
    let nameError = "";
    if (name === "") nameError = "A name is required.";
    else if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) {
      nameError =
        "Lowercase letters, digits and hyphens only, starting and ending with a letter or digit.";
    }
    const displayError = display.length > 128 ? "At most 128 characters." : "";
    this.fieldErrors = { ...this.fieldErrors, name: nameError, display_name: displayError };
    if (nameError || displayError) return;

    const patch: { name?: string; display_name?: string } = {};
    if (name !== inst.name) patch.name = name;
    if (display !== (inst.display_name ?? "")) patch.display_name = display;
    if (Object.keys(patch).length === 0) {
      toast("Nothing to save — both values already match what is stored.", "info");
      return;
    }

    this.savingIdentity = true;
    this.identityFailure = null;
    try {
      await updateInstance(this.instanceId, patch, { ifMatch: this.etag || undefined });
      // Re-read rather than trusting the response body: the read is also how the
      // next `If-Match` is obtained, and one code path for "what is stored" is one
      // fewer way for the form to disagree with the record.
      const { value, etag } = await getInstanceWithETag(this.instanceId);
      this.adopt(value, etag);
      this.fieldErrors = {};
      toast("Saved. Nothing restarted: neither of these values reaches the container.", "success");
    } catch (err) {
      const failure = parseError(err);
      this.identityFailure = failure;
      const fields: Partial<Record<"name" | "display_name", string>> = {};
      for (const f of validationFields(failure)) {
        if (f.path === "name" || f.path === "display_name") fields[f.path] = f.reason;
      }
      this.fieldErrors = { ...this.fieldErrors, ...fields };
    } finally {
      this.savingIdentity = false;
    }
  }

  /* ── the restart-class changes ─────────────────────────────────────── */

  private openConfirm(kind: "size" | "profile") {
    this.confirm = kind;
    this.acceptRestart = false;
    this.changeFailure = null;
  }

  /**
   * applyChange submits whichever restart-class change was confirmed.
   *
   * The two go through different routes on purpose. A size change is a `PATCH` on
   * the instance and is maintainer-level; the audit privacy profile is
   * `PUT …/audit-profile`, is **owner**-level, and carries the restart
   * acknowledgement in its body — because what it changes is not capacity but what
   * the platform is permitted to store, which is a disclosure decision rather than
   * an operational one (`SC-F821`, `06` §3.4).
   */
  private async applyChange() {
    const kind = this.confirm;
    if (!kind) return;
    this.applying = true;
    this.changeFailure = null;
    try {
      if (kind === "size") {
        const answer = await updateInstance(
          this.instanceId,
          { size: this.size },
          { ifMatch: this.etag || undefined },
        );
        // A material change answers `202` with `{instance, job}`; the same route
        // answers a bare record when nothing material moved, so the job is read
        // defensively rather than assumed.
        const job = (answer as { job?: Job }).job;
        this.confirm = null;
        if (job) {
          this.job = job;
          void this.watch(job.id);
        }
      } else {
        const started = await setAuditProfile(this.instanceId, this.profile, {
          allowRestart: true,
        });
        this.confirm = null;
        this.job = started.job;
        void this.watch(started.job.id);
      }
      const { value, etag } = await getInstanceWithETag(this.instanceId);
      this.adopt(value, etag);
    } catch (err) {
      this.changeFailure = parseError(err);
      const fields: Partial<Record<"size", string>> = {};
      for (const f of validationFields(this.changeFailure)) {
        if (f.path === "size") fields.size = f.reason;
      }
      this.fieldErrors = { ...this.fieldErrors, ...fields };
    } finally {
      this.applying = false;
    }
  }

  /** watch follows the change with the product's one progress helper (`I7`). */
  private async watch(jobId: string) {
    this.watchAbort?.abort();
    this.watchAbort = new AbortController();
    try {
      const done = await watchJob(jobId, {
        onUpdate: (job) => (this.job = job),
        signal: this.watchAbort.signal,
      });
      const failed = jobFailure(done);
      if (failed) {
        toastPersistent(failed.message, "error", "The change did not complete");
      } else {
        toast("The change is applied and the instance is running it.", "success");
      }
      const { value, etag } = await getInstanceWithETag(this.instanceId);
      this.adopt(value, etag);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      // The watch broke, not the job. It is still converging server-side, and the
      // permalink is where its outcome will be.
      toast(parseError(err).message, "warning");
    }
  }

  /* ── rendering ───────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderInlineFailure(err: ApiError, extra?: TemplateResult): TemplateResult {
    return html`<div role="alert" style="margin-top: var(--bino-space-md)">
      <p class="copy">${err.message}</p>
      ${extra ?? nothing}
      <bn-kv .rows=${this.supportRows(err)}></bn-kv>
    </div>`;
  }

  /** Tier 1 — cosmetic. */
  private renderIdentity(inst: Instance): TemplateResult {
    return html`<bn-card heading="Name">
      <p class="copy">
        Both of these are labels this organization uses. Neither reaches the container, and neither
        is part of any address: every cluster object is named from the instance's record id, so a
        rename is not a cluster operation and nothing restarts.
      </p>
      <div class="fields">
        <bn-field
          label="Name"
          hint="Unique within this organization. Lowercase letters, digits and hyphens."
          .error=${this.fieldErrors.name ?? ""}
          required
        >
          <bn-input
            mono
            .value=${this.name}
            ?disabled=${!this.canMaintain}
            ?invalid=${!!this.fieldErrors.name}
            @input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Display name"
          hint="Free text, up to 128 characters. Shown in lists and headings where the short name is too terse."
          .error=${this.fieldErrors.display_name ?? ""}
        >
          <bn-input
            .value=${this.displayName}
            ?disabled=${!this.canMaintain}
            ?invalid=${!!this.fieldErrors.display_name}
            @input=${(e: CustomEvent<{ value: string }>) => (this.displayName = e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>
      <div class="actions">
        <bn-button
          variant="primary"
          ?disabled=${!this.canMaintain}
          ?loading=${this.savingIdentity}
          @click=${() => void this.saveIdentity()}
        >
          Save
        </bn-button>
        ${!this.canMaintain
          ? html`<span class="muted">Renaming needs the maintainer role on this organization.</span>`
          : nothing}
      </div>
      ${this.identityFailure
        ? this.renderInlineFailure(
            this.identityFailure,
            this.identityFailure.code === "precondition_failed"
              ? html`<p class="copy">
                  Somebody else changed this instance since the page loaded, so the edit was refused
                  rather than applied over theirs. Reload to pick up their change, then re-apply
                  yours.
                </p>`
              : undefined,
          )
        : nothing}

      <bn-kv
        style="margin-top: var(--bino-space-lg)"
        .rows=${[
          { label: "Slug", value: inst.slug },
          { label: "Hostname", value: inst.hostname },
          { label: "Instance id", value: inst.id },
          { label: "Organization id", value: inst.org },
        ] as BnKvRow[]}
      ></bn-kv>
      <p class="muted">
        The slug and the hostname are fixed for the life of the instance. The slug is a single DNS
        label under the zone and is unique across the whole zone, so renaming the instance
        deliberately does not move its endpoint, and no client has to be reconfigured.
      </p>
    </bn-card>`;
  }

  /** Tier 2a — the size class (`SC-F309`, `SC-S301`). */
  private renderCapacity(inst: Instance): TemplateResult {
    const options: BnSelectOption[] = INSTANCE_SIZES.map((s) => ({
      value: s.size,
      label: s.label,
      description: `${s.cpu} · ${s.memory} · sized for about ${s.sustained_queries_per_day.toLocaleString()} queries a day`,
    }));
    const chosen = sizeClass(this.size);
    const dedicated = sizeClass(inst.size) === undefined;
    const dirty = this.size !== inst.size;
    return html`<bn-card heading="Size">
      <div class="row" style="margin-bottom: var(--bino-space-md)">
        <bn-badge variant="restart">Restart-class</bn-badge>
        <span class="muted" style="margin: 0">A size change replaces the running task.</span>
      </div>
      <p class="copy">
        Size is <strong>one knob</strong>. The container's CPU and memory limits, its process limit,
        its scheduling reservations, DuckDB's own memory limit, its thread count and the connection
        pool size all come from one row keyed by the size you pick. They are derived together
        because setting them independently is how a container gets killed instead of spilling to
        disk, and how a thread count exceeds the scheduled cores and produces throttling rather than
        throughput — two silent misconfigurations that look like Sluice bugs.
      </p>
      <p class="copy">
        There is <strong>no replica control</strong>, here or anywhere in the product. One replica per
        instance is an invariant: a Sluice process owns its hash-chained audit file and its approval
        and budget stores exclusively, and two writers is corruption. Capacity is vertical only.
      </p>
      ${dedicated
        ? html`<bn-kv .rows=${[{ label: "Size class", value: inst.size }] as BnKvRow[]}></bn-kv>
            <p class="muted">
              This instance runs on a dedicated tier, whose numbers are per-pool rather than from the
              shared table, so they are not shown here. Changing a dedicated tier is done with
              support.
            </p>`
        : html`
            <div class="fields">
              <bn-field
                label="Size class"
                .error=${this.fieldErrors.size ?? ""}
                hint="Currently running: ${inst.size}."
              >
                <bn-select
                  .options=${options}
                  .value=${this.size}
                  ?disabled=${!this.canMaintain}
                  @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                    (this.size = e.detail.value as InstanceSize)}
                ></bn-select>
              </bn-field>
            </div>
            ${chosen
              ? html`<bn-panel eyebrow="What this size derives" style="margin-top: var(--bino-space-md)">
                  <bn-kv
                    .rows=${[
                      { label: "Container CPU", value: `${chosen.cpu} (reserved ${chosen.cpu_reserved})` },
                      {
                        label: "Container memory",
                        value: `${chosen.memory} (reserved ${chosen.memory_reserved})`,
                      },
                      { label: "duckdb.memoryLimit", value: chosen.duckdb_memory },
                      { label: "duckdb.threads", value: String(chosen.duckdb_threads) },
                      { label: "duckdb.maxOpen", value: String(chosen.duckdb_max_open) },
                      {
                        label: "State directory",
                        value: `${chosen.state_volume} (planning figure, no per-instance quota on this deployment)`,
                      },
                      { label: "Query spill", value: "spills into the state directory" },
                    ] as BnKvRow[]}
                  ></bn-kv>
                  <p class="muted">
                    These are read-only everywhere in the product, and the server settings editor
                    refuses <code>duckdb.memoryLimit</code> and <code>duckdb.threads</code> as
                    control-plane-owned for exactly this reason: the value that makes them safe is
                    the container limit beside them.
                  </p>
                </bn-panel>`
              : nothing}
            <div class="actions">
              <bn-button
                variant="primary"
                ?disabled=${!dirty || !this.canMaintain || this.applying}
                @click=${() => this.openConfirm("size")}
              >
                Change size
              </bn-button>
              ${dirty
                ? html`<bn-button ?disabled=${this.applying} @click=${() => (this.size = inst.size)}>
                    Reset
                  </bn-button>`
                : nothing}
              ${!this.canMaintain
                ? html`<span class="muted">
                    Changing the size needs the maintainer role on this organization.
                  </span>`
                : nothing}
            </div>
          `}
    </bn-card>`;
  }

  /**
   * The selected profile's full disclosure, rendered under the picker rather than
   * inside it.
   *
   * `SC-S812` requires the `SubjectBinding` email-mapping advisory to sit next to
   * the Minimized option, and it appears twice on purpose: once as the option's own
   * one-line description in the list, and once here in full. Minimized is the option
   * a reader picks *because* they believe it removes personal data, and the one thing
   * it cannot remove is a mapping the customer authored themselves — so the caveat
   * has to be readable without opening a dropdown.
   */
  private renderProfileDisclosure(): TemplateResult | typeof nothing {
    const p = PROFILES.find((entry) => entry.value === this.profile);
    if (!p) return nothing;
    return html`<div class="disclosure">
      <p><strong>${p.label}</strong> — ${p.stores}</p>
      <span class="sample">Renders ${p.sample}</span>
      ${p.unavailableReason ? html`<p>${p.unavailableReason}</p>` : nothing}
      ${p.value === "minimized"
        ? html`<p>
            <strong>Minimized cannot undo a mapping you authored.</strong> If a subject binding maps
            an end user's email address into the record's subject identity, Minimized still stores
            it — it removes the SQL text and nothing else. Review your
            <a class="inline" href=${hrefFor("instances", this.instanceId, "subject-bindings")}
              >subject bindings</a
            >
            alongside this choice.
          </p>`
        : nothing}
    </div>`;
  }

  /** Tier 2b — the audit privacy profile (`SC-F821`, `SC-S812`). */
  private renderProfile(inst: Instance): TemplateResult {
    const dirty = this.profile !== inst.audit_profile;
    return html`<bn-card heading="Audit privacy profile">
      <div class="row" style="margin-bottom: var(--bino-space-md)">
        <bn-badge variant="restart">Restart-class</bn-badge>
        <bn-badge variant="neutral">Owner only</bn-badge>
        <span class="muted" style="margin: 0">
          The profile is rendered into <code>server.yaml</code>, and nothing in that file
          hot-reloads.
        </span>
      </div>
      <p class="copy">
        This is the answer to "what does Sluice Cloud keep about my queries". It is
        <strong>a generation-time setting</strong>, not a filter we apply on arrival: the profile is
        derived into the instance's own <code>audit.sqlSampleBytes</code>, so under Minimized the SQL
        text is never written into a record in the first place. There is deliberately no cloud-side
        redaction step, because a promise to delete something on arrival is a promise you have no way
        to check — and because a record that reached us minus the text is verifiable, while a record
        we edited is not: the hash chain covers the canonical bytes the instance wrote.
      </p>
      <div class="fields">
        <bn-field label="Profile" hint=${`Currently in force: ${profileLabel(inst.audit_profile)}.`}>
          <bn-select
            .options=${PROFILES.map(
              (p): BnSelectOption => ({
                value: p.value,
                label: p.label,
                description: p.option,
                disabled: !p.available,
                disabledReason: p.unavailableReason,
              }),
            )}
            .value=${this.profile}
            ?disabled=${!this.canOwn}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              (this.profile = e.detail.value as AuditProfile)}
          ></bn-select>
        </bn-field>
      </div>
      ${this.renderProfileDisclosure()}
      <p class="muted">
        Under Minimized, everything that does not depend on the text keeps working — every dashboard,
        every filter and the query fingerprint, which is not the text. The two surfaces that do
        depend on it, the explorer's text search and the denial reconstruction, say that they are
        unavailable <em>by this organization's own choice</em> rather than rendering empty.
      </p>
      <div class="actions">
        <bn-button
          variant="primary"
          ?disabled=${!dirty || !this.canOwn || this.applying}
          @click=${() => this.openConfirm("profile")}
        >
          Change profile
        </bn-button>
        ${dirty
          ? html`<bn-button
              ?disabled=${this.applying}
              @click=${() => (this.profile = inst.audit_profile)}
            >
              Reset
            </bn-button>`
          : nothing}
        ${!this.canOwn
          ? html`<span class="muted">
              Changing the profile needs the owner role on this organization: it decides what the
              platform is permitted to store, which is a disclosure decision rather than an
              operational one.
            </span>`
          : nothing}
      </div>
    </bn-card>`;
  }

  /** The restart confirmation. Both restart-class changes share it, because they
   * cost the same thing and the reader deserves the same sentence about it. */
  private renderConfirm(inst: Instance): TemplateResult | typeof nothing {
    const kind = this.confirm;
    if (!kind) return nothing;
    const sizeChange = kind === "size";
    const heading = sizeChange
      ? `Change ${inst.name} from ${inst.size} to ${this.size}?`
      : `Change ${inst.name}'s audit privacy profile to ${profileLabel(this.profile)}?`;
    return html`<bn-modal
      open
      tone="destructive"
      .width=${640}
      heading=${heading}
      @close=${() => (this.confirm = null)}
    >
      <p class="copy">
        ${sizeChange
          ? html`The new size is written as desired state and the service is updated: a new task
              starts with the new limits, the old one drains and stops. The state volume is
              reattached, so the audit chain continues from its last record and no genesis record is
              written.`
          : html`The profile is written as desired state and
              <code>server.yaml</code> is re-rendered, which forces a new generation. Nothing in that
              file hot-reloads, so the running process is replaced.`}
      </p>
      ${!sizeChange && this.profile === "minimized"
        ? html`<p class="copy">
            <strong>Nothing recovers text that was never written.</strong> Records already stored
            keep their SQL sample; records from the moment this takes effect have none, and switching
            back later does not fill the gap.
          </p>`
        : nothing}
      ${!sizeChange && inst.audit_profile === "minimized" && this.profile === "standard"
        ? html`<p class="copy">
            From the moment this takes effect, up to 2048 bytes of each query's text is stored in the
            audit record. Records already written stay as they are.
          </p>`
        : nothing}
      <p slot="consequence">
        The task is replaced, and in-flight queries are drained before it stops. Sluice's REST drain
        is a fixed 10 seconds and its shutdown timeout is not configurable, so a query still running
        when the drain begins is cut off — which is why both query timeouts are rendered at 8
        seconds. Expect a service interruption of roughly 10 to 25 seconds, and a short window of
        <code>503</code> on readiness after the new task starts: readiness is fail-closed until the
        first datasource probe completes.
      </p>
      <div slot="consequence" style="margin-top: var(--bino-space-sm)">
        <bn-checkbox
          label="I accept the interruption this change causes"
          .checked=${this.acceptRestart}
          @change=${(e: CustomEvent<{ checked: boolean }>) =>
            (this.acceptRestart = e.detail.checked)}
        ></bn-checkbox>
      </div>
      ${this.changeFailure ? this.renderChangeFailure(this.changeFailure) : nothing}
      <bn-button slot="footer" @click=${() => (this.confirm = null)}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        ?disabled=${!this.acceptRestart}
        ?loading=${this.applying}
        @click=${() => void this.applyChange()}
      >
        ${sizeChange ? "Change size and restart" : "Change profile and restart"}
      </bn-button>
    </bn-modal>`;
  }

  /**
   * The refusal, with the two codes that need more than their catalogue copy.
   *
   * `not_implemented` and `404` on the profile route are the same fact stated two
   * ways: the route is specified in `06` §3.4 and the pinned build does not serve
   * it. Saying so is `concept.md` §7 principle 8 — the product does not claim a
   * capability the build does not have, and it does not blame the reader for a
   * platform gap either.
   */
  private renderChangeFailure(err: ApiError): TemplateResult {
    let extra: TemplateResult | undefined;
    if (err.code === "instance_busy") {
      extra = html`<p class="copy">
        Exactly one mutating operation runs against an instance at a time, so that desired and
        observed state cannot diverge under concurrency. Wait for the operation in flight — it is in
        <a class="inline" href=${hrefFor("instances", this.instanceId, "jobs")}>the job history</a> —
        and try again.
      </p>`;
    } else if (err.code === "not_found" || err.code === "internal" || err.status === 501) {
      extra = html`<p class="copy">
        This change is specified and is <strong>not served by this build</strong>. Until the route
        lands, an audit privacy profile is changed by support so that the restart it causes has a
        person attached to it. The profile in force is shown above and is what the instance is
        running.
      </p>`;
    } else if (err.code === "restart_required") {
      extra = html`<p class="copy">
        The acknowledgement did not reach the server. Tick the box above and try again — the route
        refuses a profile change that does not carry an explicit acknowledgement of the restart,
        rather than restarting the instance behind an operator's back.
      </p>`;
    }
    return this.renderInlineFailure(err, extra);
  }

  /** The in-flight restart-class change. The permalink is named here because this
   * page is not where the reader should have to wait. */
  private renderJob(): TemplateResult | typeof nothing {
    const job = this.job;
    if (!job) return nothing;
    return html`<bn-panel eyebrow="Change in progress">
      <div class="row">
        ${job.status === "queued" || job.status === "running"
          ? html`<bn-spinner></bn-spinner>`
          : nothing}
        <span>
          ${job.progress
            ? `${job.progress.step} — step ${job.progress.index} of ${job.progress.total}`
            : `Job ${job.status}`}
        </span>
        <a class="inline" href=${hrefFor("jobs", job.id)}>Open job</a>
      </div>
      <p class="muted">
        The change continues if you leave this page: it runs in the control plane, not in your
        session. The job link above is where its outcome will be, hours from now.
      </p>
    </bn-panel>`;
  }

  /** Tier 3 — the danger zone. See the class doc for why it hands the ceremony to
   * the console's own danger tab rather than repeating it. */
  private renderDanger(inst: Instance): TemplateResult {
    const dangerHref = `${hrefFor("instances", inst.id)}#danger`;
    const suspended = inst.desired_state === "suspended";
    return html`<div>
      <h2 style="margin-bottom: var(--bino-space-md)">Danger zone</h2>
      <div class="danger-card">
        <h2>Restart</h2>
        <p class="copy">
          Recreates the task without touching the service spec, so a restart creates no generation
          and is not a publish. Needs the maintainer role.
        </p>
        <div class="actions">
          <bn-button variant="danger" @click=${() => navigate(dangerHref)}>
            Go to restart
          </bn-button>
        </div>
      </div>
      <div class="danger-card">
        <h2>${suspended ? "Resume" : "Suspend"}</h2>
        <p class="copy">
          ${suspended
            ? "Returns the instance to one replica on the same volume. The audit chain continues from its last record; no genesis record is written."
            : "Scales the service to zero replicas. The volume, the configs and the secrets are retained; the endpoint stops answering. A suspended instance is excluded from running-instance quota and still counted for stored state."}
          Needs the owner role.
        </p>
        <div class="actions">
          <bn-button variant="danger" @click=${() => navigate(dangerHref)}>
            Go to ${suspended ? "resume" : "suspend"}
          </bn-button>
        </div>
      </div>
      <div class="danger-card">
        <h2>Delete</h2>
        <p class="copy">
          Irreversible, and not instant: the record moves to <strong>deleting</strong> and stays
          there while the cluster converges and a node-local agent confirms every volume was
          reclaimed. Nothing claims the data is gone before that confirmation arrives. Audit records
          already ingested are retained and stay searchable; the slug stays quarantined so the
          hostname cannot be reissued. Needs the owner role.
        </p>
        <div class="actions">
          <bn-button variant="danger" @click=${() => navigate(dangerHref)}>Go to delete</bn-button>
        </div>
      </div>
      <p class="muted">
        These four operations are confirmed on the instance console's danger tab, which is where
        their confirmations live — the typed instance name, the destroyed-versus-retained lists, the
        statement that deletion is asynchronous. There is one copy of that wording on purpose: with
        a destructive action, the wording <em>is</em> the safety mechanism, and two copies is how one
        of them drifts.
      </p>
    </div>`;
  }

  override render() {
    if (this.loading && !this.instance) {
      return html`<bn-spinner block size="28" label="Loading instance settings"></bn-spinner>`;
    }
    if (this.failure) {
      return html`
        <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="These settings could not be loaded">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>
      `;
    }
    const inst = this.instance;
    if (!inst) {
      return html`<bn-empty-state
        icon="∅"
        heading="No instance"
        message="This page was opened without an instance id."
      ></bn-empty-state>`;
    }
    const gone = inst.desired_state === "deleted" || inst.observed_state === "deleting";

    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <h1>Settings</h1>
        <p class="lede">
          Everything on this page is the instance's <strong>desired state</strong> — what you are
          asking for. The cluster is a projection of it, and the two are shown side by side on the
          console rather than fused into one truth. Each section says what changing it costs before
          the button, not after the save.
        </p>
      </header>

      <div class="stack">
        ${gone
          ? html`<bn-panel eyebrow="Being deleted">
              <p class="copy">
                This instance is being torn down, so its settings are no longer editable: writing
                intent nothing will converge on would be a lie the console immediately contradicts.
                Teardown progress is on
                <a class="inline" href=${hrefFor("instances", this.instanceId)}>the overview</a>.
              </p>
            </bn-panel>`
          : nothing}
        ${this.renderJob()} ${this.renderIdentity(inst)} ${this.renderCapacity(inst)}
        ${this.renderProfile(inst)}
        ${this.changeFailure && !this.confirm ? this.renderChangeFailure(this.changeFailure) : nothing}
        <bn-panel eyebrow="Settings that are not here">
          <p class="copy">
            The pinned Sluice version and the image digest are read-only on
            <a class="inline" href=${`${hrefFor("instances", this.instanceId)}#settings`}
              >the console's settings tab</a
            >: per-instance upgrade with rollback is a later release, so until it lands the version
            is changed by support and a failed upgrade has a person attached to it.
          </p>
          <p class="copy">
            The tunables inside <code>server.yaml</code> — query timeouts, row and byte limits,
            concurrency, the rewrite cache, the policy engine, tracing, approvals and budgets — are
            on
            <a class="inline" href=${hrefFor("instances", this.instanceId, "server-config")}
              >server settings</a
            >. Datasources, policies and subject bindings are structured records with their own
            editors, and they ship through the publish flow rather than being saved here.
          </p>
        </bn-panel>
        ${this.renderDanger(inst)}
      </div>
      ${this.renderConfirm(inst)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-instance-settings": PageInstanceSettings;
  }
}
